import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MasterCropData, MasterUPCData, MasterVariantData, SectionTarget } from '../types';

// ── Shared style ──

export function actionBtnStyle(color: string): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px',
    background: 'transparent', border: `1px solid ${color}`, borderRadius: 'var(--radius-sm)',
    color, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
  };
}

// ── Types ──

export interface PendingChanges {
  merges: { sourceVariantId: string; targetVariantId: string }[];
  moves: { cropId: string; targetVariantId: string; targetItemNumber: string; targetSection: SectionTarget['type'] }[];
  deactivations: string[];
  reactivations: string[];
  differentItems: string[];
  newVariants: { id: string; cropIds: string[]; itemNumber: string }[];
  representativeChanges: { variantId: string; cropId: string }[];
}

export function emptyChanges(): PendingChanges {
  return { merges: [], moves: [], deactivations: [], reactivations: [], differentItems: [], newVariants: [], representativeChanges: [] };
}

export interface UndoEntry {
  label: string;
  upcData: MasterUPCData[];
  changes: PendingChanges;
  extra?: unknown;
}

export type ActionMode = 'idle' | 'crops-selected' | 'merge-source';

const VARIANT_COLORS = ['#f87171', '#60a5fa', '#34d399', '#c084fc', '#fbbf24', '#f472b6', '#2dd4bf', '#fb923c'];

export interface UseUPCActionsOptions {
  initialUpcData: MasterUPCData[];
  notify: (text: string, type?: 'success' | 'warning' | 'error') => void;
  /** Called when changes update (for syncing to parent) */
  onChangesUpdate?: (changes: PendingChanges) => void;
  /** Extra data to save/restore in undo entries */
  saveUndoExtra?: () => unknown;
  restoreUndoExtra?: (extra: unknown) => void;
}

export function useUPCActions({
  initialUpcData,
  notify,
  onChangesUpdate,
  saveUndoExtra,
  restoreUndoExtra,
}: UseUPCActionsOptions) {
  const [upcData, setUpcData] = useState<MasterUPCData[]>(() => structuredClone(initialUpcData));
  const [selectedCropIds, setSelectedCropIds] = useState<Set<string>>(new Set());
  const [mergeSourceVariantId, setMergeSourceVariantId] = useState<string | null>(null);
  const [activeVariantId, setActiveVariantId] = useState<string | null>(null);
  const [changes, setChanges] = useState<PendingChanges>(emptyChanges);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);

  const [recentlyMovedCropIds, setRecentlyMovedCropIds] = useState<Set<string>>(new Set());
  const movedTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const markMoved = useCallback((cropIds: string[]) => {
    if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
    setRecentlyMovedCropIds(new Set(cropIds));
    movedTimerRef.current = setTimeout(() => setRecentlyMovedCropIds(new Set()), 2000);
  }, []);

  const mode: ActionMode = selectedCropIds.size > 0
    ? 'crops-selected'
    : mergeSourceVariantId
      ? 'merge-source'
      : 'idle';

  // Clear active variant when leaving idle mode
  useEffect(() => { if (mode !== 'idle') setActiveVariantId(null); }, [mode]);

  // Sync changes to parent
  useEffect(() => { onChangesUpdate?.(changes); }, [changes, onChangesUpdate]);

  // ── Variant color map ──

  const variantColorMap = useMemo(() => {
    const map = new Map<string, string>();
    let idx = 0;
    for (const u of upcData) {
      for (const v of u.variants) {
        if (!map.has(v.id)) {
          map.set(v.id, VARIANT_COLORS[idx % VARIANT_COLORS.length]);
          idx++;
        }
      }
    }
    return map;
  }, [upcData]);

  // ── Undo ──

  const pushUndo = useCallback((label: string) => {
    setUndoStack(prev => [...prev, {
      label,
      upcData: structuredClone(upcData),
      changes: structuredClone(changes),
      extra: saveUndoExtra?.(),
    }]);
  }, [upcData, changes, saveUndoExtra]);

  const undo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setUpcData(last.upcData);
      setChanges(last.changes);
      setSelectedCropIds(new Set());
      setMergeSourceVariantId(null);
      if (last.extra !== undefined) restoreUndoExtra?.(last.extra);
      notify(`Undid: ${last.label}`, 'warning');
      return prev.slice(0, -1);
    });
  }, [notify, restoreUndoExtra]);

  // ── Helpers ──

  const findCropLocation = useCallback((cropId: string, data: MasterUPCData[]): { upcIdx: number; section: 'variant' | 'no-variant' | 'non-front' | 'carton' | 'inactive'; variantIdx?: number; cropIdx: number } | null => {
    for (let ui = 0; ui < data.length; ui++) {
      const u = data[ui];
      for (let vi = 0; vi < u.variants.length; vi++) {
        const ci = u.variants[vi].crops.findIndex(c => c.id === cropId);
        if (ci !== -1) return { upcIdx: ui, section: 'variant', variantIdx: vi, cropIdx: ci };
      }
      if (u.pendingCrops) {
        const pi = u.pendingCrops.findIndex(c => c.id === cropId);
        if (pi !== -1) return { upcIdx: ui, section: 'no-variant', cropIdx: pi };
      }
      const uci = u.unassignedCrops.findIndex(c => c.id === cropId);
      if (uci !== -1) return { upcIdx: ui, section: 'no-variant', cropIdx: uci };
      const nfi = u.nonFrontCrops.findIndex(c => c.id === cropId);
      if (nfi !== -1) return { upcIdx: ui, section: 'non-front', cropIdx: nfi };
      const cti = u.cartonCrops.findIndex(c => c.id === cropId);
      if (cti !== -1) return { upcIdx: ui, section: 'carton', cropIdx: cti };
      if (u.inactiveCrops) {
        const ii = u.inactiveCrops.findIndex(c => c.id === cropId);
        if (ii !== -1) return { upcIdx: ui, section: 'inactive', cropIdx: ii };
      }
    }
    return null;
  }, []);

  /** Check if a crop needs server-side reactivation (inactive or pending crops are !isActive on the server) */
  const needsReactivation = useCallback((cropId: string, data: MasterUPCData[]): boolean => {
    const loc = findCropLocation(cropId, data);
    if (!loc) return false;
    if (loc.section === 'inactive') return true;
    // Pending crops are isActive=false on server
    const u = data[loc.upcIdx];
    if (u.pendingCrops) {
      const crop = u.pendingCrops.find(c => c.id === cropId);
      if (crop?.isPending) return true;
    }
    return false;
  }, [findCropLocation]);

  // ── Helpers: safe crop removal (always finds by ID, avoids stale-index bugs) ──

  /** Remove a crop by ID from any section in the data array. Returns the removed crop or null. */
  const removeCropById = (cropId: string, data: MasterUPCData[]): MasterCropData | null => {
    for (const u of data) {
      for (const v of u.variants) {
        const ci = v.crops.findIndex(c => c.id === cropId);
        if (ci !== -1) return v.crops.splice(ci, 1)[0];
      }
      if (u.pendingCrops) {
        const pi = u.pendingCrops.findIndex(c => c.id === cropId);
        if (pi !== -1) return u.pendingCrops.splice(pi, 1)[0];
      }
      const ui = u.unassignedCrops.findIndex(c => c.id === cropId);
      if (ui !== -1) return u.unassignedCrops.splice(ui, 1)[0];
      const nfi = u.nonFrontCrops.findIndex(c => c.id === cropId);
      if (nfi !== -1) return u.nonFrontCrops.splice(nfi, 1)[0];
      const cti = u.cartonCrops.findIndex(c => c.id === cropId);
      if (cti !== -1) return u.cartonCrops.splice(cti, 1)[0];
      if (u.inactiveCrops) {
        const ii = u.inactiveCrops.findIndex(c => c.id === cropId);
        if (ii !== -1) {
          const crop = u.inactiveCrops.splice(ii, 1)[0];
          crop.isInactive = false;
          return crop;
        }
      }
    }
    return null;
  };

  // ── Actions ──

  const executeMove = useCallback((cropIds: Set<string>, target: SectionTarget) => {
    pushUndo(`Move ${cropIds.size} crop(s)`);

    const sourceVariantIds = new Set<string>();
    const reactivatedIds: string[] = [];
    for (const cropId of cropIds) {
      const loc = findCropLocation(cropId, upcData);
      if (loc?.section === 'variant' && loc.variantIdx !== undefined) {
        sourceVariantIds.add(upcData[loc.upcIdx].variants[loc.variantIdx].id);
      }
      if (needsReactivation(cropId, upcData)) reactivatedIds.push(cropId);
    }

    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      const cropsToMove: MasterCropData[] = [];

      for (const cropId of cropIds) {
        const crop = removeCropById(cropId, next);
        if (crop) cropsToMove.push(crop);
      }

      const emptiedVariantIds: string[] = [];
      for (const u of next) {
        for (const v of u.variants) {
          if (v.crops.length === 0 && sourceVariantIds.has(v.id)) {
            emptiedVariantIds.push(v.id);
          }
        }
        u.variants = u.variants.filter(v => v.crops.length > 0);
      }

      const targetItemData = next.find((u) => u.itemNumber === target.itemNumber);
      if (!targetItemData) return prev;

      if (target.type === 'variant' && target.variantId) {
        const v = targetItemData.variants.find(v => v.id === target.variantId);
        for (const crop of cropsToMove) {
          crop.isInactive = false;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        if (v) v.crops.push(...cropsToMove);
      } else if (target.type === 'no-variant') {
        for (const crop of cropsToMove) {
          crop.isInactive = false;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        targetItemData.unassignedCrops.push(...cropsToMove);
      } else if (target.type === 'non-front') {
        for (const crop of cropsToMove) {
          crop.isInactive = true;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        targetItemData.nonFrontCrops.push(...cropsToMove);
      } else if (target.type === 'carton') {
        for (const crop of cropsToMove) {
          crop.isInactive = false;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        targetItemData.cartonCrops.push(...cropsToMove);
      } else if (target.type === 'inactive') {
        for (const crop of cropsToMove) {
          crop.isInactive = true;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        if (!targetItemData.inactiveCrops) targetItemData.inactiveCrops = [];
        targetItemData.inactiveCrops.push(...cropsToMove);
      }

      if (target.type === 'variant' && target.variantId && emptiedVariantIds.length > 0) {
        setChanges(ch => ({
          ...ch,
          merges: [...ch.merges, ...emptiedVariantIds.map(id => ({ sourceVariantId: id, targetVariantId: target.variantId! }))],
        }));
        for (const id of emptiedVariantIds) {
          notify(`Auto-merged ${id.slice(0, 8)} → ${target.variantId.slice(0, 8)}`);
        }
      }

      return next;
    });
    setChanges(prev => ({
      ...prev,
      moves: [...prev.moves, ...[...cropIds].map(cropId => ({
        cropId, targetVariantId: target.variantId || '', targetItemNumber: target.itemNumber, targetSection: target.type,
      }))],
      reactivations: reactivatedIds.length > 0 ? [...prev.reactivations, ...reactivatedIds] : prev.reactivations,
    }));
    setSelectedCropIds(new Set());
    markMoved([...cropIds]);
    notify(`Moved ${cropIds.size} crop${cropIds.size > 1 ? 's' : ''}`);
  }, [findCropLocation, needsReactivation, upcData, notify, pushUndo, markMoved]);

  const executeMerge = useCallback((sourceId: string, targetId: string) => {
    pushUndo(`Merge ${sourceId.slice(0, 8)} → ${targetId.slice(0, 8)}`);
    // Collect source crop IDs before mutating state
    const sourceCropIds: string[] = [];
    for (const u of upcData) {
      const v = u.variants.find(v => v.id === sourceId);
      if (v) { sourceCropIds.push(...v.crops.map(c => c.id)); break; }
    }
    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      let sourceVariant: MasterVariantData | null = null;
      let sourceUpcIdx = -1;
      let sourceVariantIdx = -1;

      for (let ui = 0; ui < next.length; ui++) {
        const vi = next[ui].variants.findIndex(v => v.id === sourceId);
        if (vi !== -1) {
          sourceVariant = next[ui].variants[vi];
          sourceUpcIdx = ui;
          sourceVariantIdx = vi;
          break;
        }
      }
      if (!sourceVariant) return prev;

      let targetVariant: MasterVariantData | null = null;
      for (const u of next) {
        const v = u.variants.find(v => v.id === targetId);
        if (v) { targetVariant = v; break; }
      }
      if (!targetVariant) return prev;

      targetVariant.crops.push(...sourceVariant.crops);
      next[sourceUpcIdx].variants.splice(sourceVariantIdx, 1);
      return next;
    });
    setChanges(prev => ({ ...prev, merges: [...prev.merges, { sourceVariantId: sourceId, targetVariantId: targetId }] }));
    setMergeSourceVariantId(null);
    markMoved(sourceCropIds);
    notify(`Merged ${sourceId.slice(0, 8)} → ${targetId.slice(0, 8)}`);
  }, [upcData, notify, pushUndo, markMoved]);

  const deactivateCrops = useCallback(() => {
    const ids = [...selectedCropIds];
    const idSet = new Set(ids);
    pushUndo(`Deactivate ${ids.length} crop(s)`);
    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      for (const u of next) {
        const deactivated: MasterCropData[] = [];
        for (const v of u.variants) {
          const removed = v.crops.filter(c => idSet.has(c.id));
          v.crops = v.crops.filter(c => !idSet.has(c.id));
          deactivated.push(...removed);
        }
        u.variants = u.variants.filter(v => v.crops.length > 0);
        if (u.pendingCrops) {
          deactivated.push(...u.pendingCrops.filter(c => idSet.has(c.id)));
          u.pendingCrops = u.pendingCrops.filter(c => !idSet.has(c.id));
        }
        deactivated.push(...u.unassignedCrops.filter(c => idSet.has(c.id)));
        u.unassignedCrops = u.unassignedCrops.filter(c => !idSet.has(c.id));
        deactivated.push(...u.nonFrontCrops.filter(c => idSet.has(c.id)));
        u.nonFrontCrops = u.nonFrontCrops.filter(c => !idSet.has(c.id));
        deactivated.push(...u.cartonCrops.filter(c => idSet.has(c.id)));
        u.cartonCrops = u.cartonCrops.filter(c => !idSet.has(c.id));
        for (const crop of deactivated) {
          crop.isInactive = true;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        if (!u.inactiveCrops) u.inactiveCrops = [];
        u.inactiveCrops.push(...deactivated);
      }
      return next;
    });
    setChanges(prev => ({ ...prev, deactivations: [...prev.deactivations, ...ids] }));
    setSelectedCropIds(new Set());
    markMoved(ids);
    notify(`Deactivated ${ids.length} crop${ids.length > 1 ? 's' : ''}`, 'warning');
  }, [selectedCropIds, notify, pushUndo, markMoved]);

  const markDifferentItem = useCallback(() => {
    const ids = [...selectedCropIds];
    const idSet = new Set(ids);
    pushUndo(`Different item ${ids.length} crop(s)`);
    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      for (const u of next) {
        for (const v of u.variants) v.crops = v.crops.filter(c => !idSet.has(c.id));
        u.variants = u.variants.filter(v => v.crops.length > 0);
        if (u.pendingCrops) u.pendingCrops = u.pendingCrops.filter(c => !idSet.has(c.id));
        u.unassignedCrops = u.unassignedCrops.filter(c => !idSet.has(c.id));
        u.nonFrontCrops = u.nonFrontCrops.filter(c => !idSet.has(c.id));
        u.cartonCrops = u.cartonCrops.filter(c => !idSet.has(c.id));
        if (u.inactiveCrops) u.inactiveCrops = u.inactiveCrops.filter(c => !idSet.has(c.id));
      }
      return next;
    });
    setChanges(prev => ({ ...prev, differentItems: [...prev.differentItems, ...ids] }));
    setSelectedCropIds(new Set());
    notify(`Marked ${ids.length} crop${ids.length > 1 ? 's' : ''} as different item`);
  }, [selectedCropIds, notify, pushUndo]);

  const createNewVariant = useCallback((itemNumber: string) => {
    const ids = [...selectedCropIds];
    const newId = `new-${crypto.randomUUID().slice(0, 8)}`;
    const reactivatedIds = ids.filter(cropId => needsReactivation(cropId, upcData));
    pushUndo(`Create variant ${newId}`);
    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      const cropsToMove: MasterCropData[] = [];
      for (const cropId of ids) {
        const crop = removeCropById(cropId, next);
        if (crop) cropsToMove.push(crop);
      }
      for (const u of next) u.variants = u.variants.filter(v => v.crops.length > 0);
      const targetItem = next.find((u) => u.itemNumber === itemNumber);
      if (targetItem && cropsToMove.length > 0) {
        targetItem.variants.push({
          id: newId,
          itemNumber: targetItem.itemNumber,
          representativeCropId: cropsToMove[0].id,
          barcodeStatus: 'unconfirmed',
          crops: cropsToMove,
        });
      }
      return next;
    });
    setChanges(prev => ({
      ...prev,
      newVariants: [...prev.newVariants, { id: newId, cropIds: ids, itemNumber }],
      reactivations: reactivatedIds.length > 0 ? [...prev.reactivations, ...reactivatedIds] : prev.reactivations,
    }));
    setSelectedCropIds(new Set());
    markMoved(ids);
    notify(`Created variant ${newId}`);
  }, [selectedCropIds, needsReactivation, upcData, notify, pushUndo, markMoved]);

  const setRepresentative = useCallback((variantId: string, cropId: string) => {
    pushUndo(`Set rep for ${variantId.slice(0, 8)}`);
    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      for (const u of next) {
        const v = u.variants.find(v => v.id === variantId);
        if (v) { v.representativeCropId = cropId; break; }
      }
      return next;
    });
    setChanges(prev => ({ ...prev, representativeChanges: [...prev.representativeChanges, { variantId, cropId }] }));
    setSelectedCropIds(new Set());
    notify(`Set representative for ${variantId.slice(0, 8)}`);
  }, [notify, pushUndo]);

  // ── Section click (move / merge target) ──

  const handleSectionClick = useCallback((target: SectionTarget) => {
    if (mode === 'crops-selected') {
      // Can't move crops into pending
      if (target.type === 'pending') return;
      // Don't move if all selected crops are already in the target section
      const allAlreadyInTarget = [...selectedCropIds].every(cropId => {
        const loc = findCropLocation(cropId, upcData);
        if (!loc) return false;
        const locItemNumber = upcData[loc.upcIdx].itemNumber;
        if (locItemNumber !== target.itemNumber) return false;
        if (target.type === 'variant' && target.variantId && loc.section === 'variant' && loc.variantIdx !== undefined) {
          return upcData[loc.upcIdx].variants[loc.variantIdx].id === target.variantId;
        }
        return loc.section === target.type;
      });
      if (allAlreadyInTarget) {
        setSelectedCropIds(new Set());
        return;
      }
      executeMove(new Set(selectedCropIds), target);
    } else if (mode === 'merge-source' && target.type === 'variant' && target.variantId) {
      if (target.variantId === mergeSourceVariantId) {
        setMergeSourceVariantId(null);
      } else {
        executeMerge(mergeSourceVariantId!, target.variantId);
      }
    } else if (mode === 'idle') {
      const itemEntry = upcData.find((u) => u.itemNumber === target.itemNumber);
      if (!itemEntry) return;
      let cropIds: string[] = [];
      if (target.type === 'variant' && target.variantId) {
        const v = itemEntry.variants.find(v => v.id === target.variantId);
        if (v) cropIds = v.crops.filter(c => !c.isInactive).map(c => c.id);
      } else if (target.type === 'pending') {
        cropIds = (itemEntry.pendingCrops ?? []).map(c => c.id);
      } else if (target.type === 'no-variant') {
        cropIds = itemEntry.unassignedCrops.filter(c => !c.isInactive).map(c => c.id);
      } else if (target.type === 'non-front') {
        cropIds = itemEntry.nonFrontCrops.filter(c => !c.isInactive).map(c => c.id);
      } else if (target.type === 'carton') {
        cropIds = itemEntry.cartonCrops.filter(c => !c.isInactive).map(c => c.id);
      } else if (target.type === 'inactive') {
        cropIds = (itemEntry.inactiveCrops ?? []).map(c => c.id);
      }
      if (cropIds.length > 0) setSelectedCropIds(new Set(cropIds));
    }
  }, [
    mode,
    selectedCropIds,
    mergeSourceVariantId,
    upcData,
    executeMove,
    executeMerge,
    findCropLocation,
  ]);

  // ── Computed values ──

  const selectedCropVariantInfo = useMemo(() => {
    if (selectedCropIds.size !== 1) return null;
    const cropId = [...selectedCropIds][0];
    for (const u of upcData) {
      for (const v of u.variants) {
        if (v.crops.some(c => c.id === cropId)) {
          return { variantId: v.id, cropId, isAlreadyRep: v.representativeCropId === cropId };
        }
      }
    }
    return null;
  }, [selectedCropIds, upcData]);

  const selectedCropItemNumber = useMemo(() => {
    if (selectedCropIds.size === 0) return null;
    const cropId = [...selectedCropIds][0];
    const loc = findCropLocation(cropId, upcData);
    return loc ? upcData[loc.upcIdx].itemNumber : null;
  }, [selectedCropIds, upcData, findCropLocation]);

  const selectedCropsSourceVariant = useMemo(() => {
    if (selectedCropIds.size === 0) return null;
    let variantId: string | null = null;
    for (const cropId of selectedCropIds) {
      let found = false;
      for (const u of upcData) {
        for (const v of u.variants) {
          if (v.crops.some(c => c.id === cropId)) {
            if (variantId === null) variantId = v.id;
            else if (variantId !== v.id) return null;
            found = true;
            break;
          }
        }
        if (found) break;
      }
      if (!found) return null;
    }
    return variantId;
  }, [selectedCropIds, upcData]);

  const lastClickedCropRef = useRef<string | null>(null);

  // Build ordered crop list for shift-select range (scan first, then ID to match display order)
  const getGroupCropIds = useCallback((cropId: string): string[] => {
    const sortedIds = (crops: MasterCropData[]) => [...crops].sort((a, b) => {
      const aScan = a.source === 'scan' ? 0 : 1;
      const bScan = b.source === 'scan' ? 0 : 1;
      if (aScan !== bScan) return aScan - bScan;
      return a.id.localeCompare(b.id);
    }).map(c => c.id);
    for (const u of upcData) {
      for (const v of u.variants) {
        if (v.crops.some(c => c.id === cropId)) return sortedIds(v.crops);
      }
      if (u.pendingCrops?.some(c => c.id === cropId)) return sortedIds(u.pendingCrops);
      if (u.unassignedCrops.some(c => c.id === cropId)) return sortedIds(u.unassignedCrops);
      if (u.nonFrontCrops.some(c => c.id === cropId)) return sortedIds(u.nonFrontCrops);
      if (u.cartonCrops.some(c => c.id === cropId)) return sortedIds(u.cartonCrops);
      if (u.inactiveCrops?.some(c => c.id === cropId)) return sortedIds(u.inactiveCrops);
    }
    return [];
  }, [upcData]);

  const toggleCropSelection = useCallback((cropId: string, shiftKey = false) => {
    if (shiftKey && lastClickedCropRef.current && lastClickedCropRef.current !== cropId) {
      const group = getGroupCropIds(cropId);
      const lastIdx = group.indexOf(lastClickedCropRef.current);
      const curIdx = group.indexOf(cropId);
      if (lastIdx !== -1 && curIdx !== -1) {
        const start = Math.min(lastIdx, curIdx);
        const end = Math.max(lastIdx, curIdx);
        const range = group.slice(start, end + 1);
        setSelectedCropIds(prev => {
          const n = new Set(prev);
          for (const id of range) n.add(id);
          return n;
        });
        lastClickedCropRef.current = cropId;
        return;
      }
    }
    lastClickedCropRef.current = cropId;
    setSelectedCropIds(prev => {
      const n = new Set(prev);
      if (n.has(cropId)) n.delete(cropId);
      else n.add(cropId);
      return n;
    });
  }, [getGroupCropIds]);

  const resetAfterSave = useCallback((freshData: MasterUPCData[]) => {
    setUpcData(freshData);
    setChanges(emptyChanges());
    setUndoStack([]);
    setSelectedCropIds(new Set());
    setMergeSourceVariantId(null);
  }, []);

  return {
    upcData,
    setUpcData,
    selectedCropIds,
    setSelectedCropIds,
    toggleCropSelection,
    mergeSourceVariantId,
    setMergeSourceVariantId,
    activeVariantId,
    setActiveVariantId,
    mode,
    changes,
    setChanges,
    variantColorMap,
    executeMove,
    executeMerge,
    deactivateCrops,
    markDifferentItem,
    createNewVariant,
    setRepresentative,
    handleSectionClick,
    findCropLocation,
    undoStack,
    undo,
    pushUndo,
    selectedCropVariantInfo,
    selectedCropItemNumber,
    selectedCropsSourceVariant,
    resetAfterSave,
    recentlyMovedCropIds,
  };
}
